import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import GUI from "lil-gui";

/**
 * Base
 */
// Debug
// const gui = new GUI();

const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();

// Canvas
const canvas = document.querySelector("canvas.webgl");

// Scene
const scene = new THREE.Scene();
scene.background = new THREE.Color(0xffffff);

// const axesHelper = new THREE.AxesHelper(5);
// scene.add(axesHelper);

let coinMoving = false;
let coinInSlot = false;
let coinCurve = null;
let coinProgress = 0;
let animationPhase = "arc"; // 'arc' or 'drop'
let dropStartY = 0;
let dropProgress = 0;

let handleRotating = false;
let rotationNumber = 1;
let handleStart = 0;
let handleProgress = 0;

let pause = false;
let pauseDuration = 0;
let pauseStartTime = 0;

let capsulesShaking = false;
let shakeProgress = 0;
let originalPositions = [];
let shakeDirections = [];
let shakeFrequencies = [];
const SHAKE_DURATION = 120;
const SHAKE_INTENSITY = 0.02;

let flapOpening = false;
let flapClosing = false;
let flapStart = 0;
let flapProgress = 0;

let colourChoice = 0;
let capsule = null;
let pivotCapsule = null;
let capsuleTopHalf = null;
let capsuleBottomHalf = null;
let capsuleMoving = false;
let capsuleCurve = null;
let capsuleProgress = 0;

let capsuleVelocity = { x: 0, y: 0, z: 0 };
let capsuleAngularVelocity = 0;
let bounceCount = 0;
let maxBounces = 0;
let gravity = 0;
let bounceDamping = 0;
let floorY = 0;

let cameraMoving = false;

let cameraLookAtTarget = new THREE.Vector3(0, 0, 0);

let capsulePresent = false;

let capsuleOpening = false;
let capsuleOpenProgress = 0;

let capsuleRotationResetting = false;
let capsuleRotationResetProgress = 0;
let capsuleStartRotation = { x: 0, y: 0, z: 0 };

let instructions = document.getElementById("home-heading");
let handleMesh = null;
let flapMesh = null;

const isMobileDevice = () => {
    return (
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
        ) ||
        (navigator.maxTouchPoints > 0 && window.innerWidth <= 1024)
    );
};
const isMobile = isMobileDevice();

let projectCards = [
    {
        title: "UKWild",
        description:
            "The design of an information-dense website to help users learn about wildlife in the UK",
        tags: ["User Research", "Information Architecture", "UX Design"],
        imagePath: "/images/ukwild/owl.jpeg",
        altText: "owl on a white background",
        buttonDestination: "ukwild",
    },
    {
        title: "Tutankhamun's Tomb",
        description:
            "The design and development of an interactive website presenting an annotated model of the tomb of Tutankhamun.",
        tags: ["UX Design", "3D", "Web Development"],
        imagePath: "/images/tomb/tomb.png",
        altText: "3D model of staircase descending into a tunnel",
        buttonDestination: "tutankhamuns-tomb",
    },
    {
        title: "Howard Carter's Slide Cabinet",
        description:
            "The design and development of an innovative website displaying archival information.",
        tags: ["UX Design", "Photogrammetry", "Web Development"],
        imagePath: "/images/cabinet/cabinet.png",
        altText: "3D model of wooden cabinet",
        buttonDestination: "carter-cabinet",
    },
    {
        title: "Harmonic Park",
        description:
            "The design of a novel music creation system to help users engage with their surroundings in Regent's park.",
        tags: ["User Research", "Conceptual Design", "Interaction Design"],
        imagePath: "/images/park-harmonics/gardens.jpeg",
        altText: "building on a lake surrounded by trees",
        buttonDestination: "harmonic-park",
    },
];

/**
 * Loading manager
 */
const loadingManager = new THREE.LoadingManager();
loadingManager.onStart = function (url, itemsLoaded, itemsTotal) {
    const loadingScreen = document.getElementById("loading-screen");
    loadingScreen.style.display = "block";
    console.log(
        "Started loading file: " +
            url +
            ".\nLoaded " +
            itemsLoaded +
            " of " +
            itemsTotal +
            " files."
    );
};

loadingManager.onLoad = function () {
    console.log("Loading complete!");
    const loadingScreen = document.getElementById("loading-screen");
    loadingScreen.classList.add("fade-out");

    setTimeout(() => {
        loadingScreen.style.display = "none";
    }, 600);
};

loadingManager.onProgress = function (url, itemsLoaded, itemsTotal) {
    console.log(
        "Loading file: " +
            url +
            ".\nLoaded " +
            itemsLoaded +
            " of " +
            itemsTotal +
            " files."
    );
};

loadingManager.onError = function (url) {
    console.log("There was an error loading " + url);
};

/**
 * Models
 */

const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath("/draco/");

const gltfLoader = new GLTFLoader(loadingManager);
gltfLoader.setDRACOLoader(dracoLoader);

const models = {
    machine: null,
    coin: null,
    capsules: null,
    handle: null,
    flap: null,
    blueCapsule: null,
    pinkCapsule: null,
    greenCapsule: null,
    yellowCapsule: null,
    pivotBlueCapsule: null,
    pivotPinkCapsule: null,
    pivotGreenCapsule: null,
    pivotYellowCapsule: null,
};

const loadModel = (name, path) => {
    return new Promise((resolve, reject) => {
        gltfLoader.load(
            path,
            (gltf) => {
                models[name] = gltf.scene;
                models[name].scale.setScalar(1);
                scene.add(models[name]);
                resolve(models[name]);
            },
            undefined,
            (error) => reject(error)
        );
    });
};

const loadCapsules = (name, path) => {
    return new Promise((resolve, reject) => {
        gltfLoader.load(
            path,
            (gltf) => {
                let allCapsules = gltf.scene;
                allCapsules.scale.setScalar(1);
                scene.add(allCapsules);
                models[name] = allCapsules.children;
                resolve(models[name]);
            },
            undefined,
            (error) => reject(error)
        );
    });
};

Promise.all([
    loadModel("machine", "/models/machine.glb"),
    loadModel("coin", "/models/coin.glb"),
    loadModel("handle", "/models/handle.glb"),
    loadModel("flap", "/models/flap.glb"),
    loadModel("blueCapsule", "/models/blue-capsule.glb"),
    loadModel("pinkCapsule", "/models/pink-capsule.glb"),
    loadModel("greenCapsule", "/models/green-capsule.glb"),
    loadModel("yellowCapsule", "/models/yellow-capsule.glb"),
    loadModel("pivotBlueCapsule", "/models/pivot-blue-capsule.glb"),
    loadModel("pivotPinkCapsule", "/models/pivot-pink-capsule.glb"),
    loadModel("pivotGreenCapsule", "/models/pivot-green-capsule.glb"),
    loadModel("pivotYellowCapsule", "/models/pivot-yellow-capsule.glb"),
    loadCapsules("capsules", "/models/capsules.glb"),
]).then(() => {
    console.log("All models loaded!");

    models.coin.position.set(0, 1, 1.75);
    handleMesh = models.handle.getObjectByName("handle");
    flapMesh = models.flap.getObjectByName("flap");
    tick();
});

/**
 * Lights
 */
const ambientLight = new THREE.AmbientLight(0xffffff, 2.4);
scene.add(ambientLight);

// strong lights
const light = new THREE.DirectionalLight(0xffffff, 2);
light.position.set(5, 5, 5);
scene.add(light);

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight,
};

window.addEventListener("resize", () => {
    // Update sizes
    sizes.width = window.innerWidth;
    sizes.height = window.innerHeight;

    // Update camera
    camera.aspect = sizes.width / sizes.height;
    camera.updateProjectionMatrix();

    // Update renderer
    renderer.setSize(sizes.width, sizes.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

const coinAudio = new Audio("/sounds/coin.mp3");
window.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();

    mouse.set(
        (e.clientX / sizes.width) * 2 - 1,
        -(e.clientY / sizes.height) * 2 + 1
    );

    raycaster.setFromCamera(mouse, camera);
    let intersects = [];
    if (models.coin) {
        intersects = raycaster.intersectObjects(scene.children, true);
    }

    if (
        intersects.length > 0 &&
        !coinMoving &&
        (intersects[0].object == models.coin.getObjectByName("Cylinder005") ||
            intersects[0].object ==
                models.coin.getObjectByName("Cylinder005_1"))
    ) {
        coinMoving = true;
        coinAudio.currentTime = 0;
        coinAudio.play();
        animationPhase = "arc";

        coinProgress = 0;
        const start = models.coin.position.clone();
        const end = new THREE.Vector3(0, 0.1, 1.75);

        const mid = new THREE.Vector3();
        mid.lerpVectors(start, end, 0.5);
        mid.y += 1; // Arc height

        coinCurve = new THREE.QuadraticBezierCurve3(start, mid, end);
        instructions.style.display = "none";
    }
});

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(
    75,
    sizes.width / sizes.height,
    0.1,
    100
);
camera.position.set(5, 2.25, 0);
scene.add(camera);

// Controls
const controls = new OrbitControls(camera, canvas);
//controls.target.set(0, 2.5, 0);
controls.target.set(0, 2.2, 0);
controls.enableDamping = true;
controls.dampingFactor = 0.06;
controls.maxDistance = 10;
controls.minDistance = 2.9;

/**
 * Renderer
 */

const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
});
//renderer.shadowMap.enabled = true;
// renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.shadowMap.enabled = false;
renderer.setSize(sizes.width, sizes.height);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.CineonToneMapping; // Often matches Blender better
renderer.toneMappingExposure = 0.8;

if (isMobile) {
    renderer.antialias = false;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
}

// Add environment map
const pmremGenerator = new THREE.PMREMGenerator(renderer);
scene.environment = pmremGenerator.fromScene(new RoomEnvironment()).texture;
scene.environmentIntensity = 0.8;

/**
 * Animate
 */
const clock = new THREE.Clock();
let previousTime = 0;

const handleAudio = new Audio("/sounds/handle.mp3");
const capsulesAudio = new Audio("/sounds/capsules.mp3");
const bounceHeight = 0.15;
const bounceSpeed = 2;
const worldZAxis = new THREE.Vector3(0, 0, 1);
const openAudio = new Audio("/sounds/open.mp3");
const capsuleAudio = new Audio("/sounds/capsule.mp3");
let capsuleOpeningSpeed = 0.02;

const tick = () => {
    const elapsedTime = clock.getElapsedTime();
    const deltaTime = elapsedTime - previousTime;
    previousTime = elapsedTime;

    if (pause) {
        let paused = elapsedTime - pauseStartTime;
        if (paused >= pauseDuration) {
            if (handleRotating) {
                rotationNumber = 2;
                handleStart = 0;
                handleProgress = 0;
                handleAudio.currentTime = 0;
                handleAudio.play();
                pause = false;
                capsulesAudio.currentTime = 0;
                capsulesAudio.play();
            }
        }
    } else if (!coinMoving && !coinInSlot) {
        models.coin.position.y =
            0.2 + Math.abs(Math.sin(elapsedTime * bounceSpeed)) * bounceHeight;
    } else if (coinMoving) {
        if (animationPhase === "arc") {
            coinProgress += 0.01; // Animation speed

            if (coinProgress >= 1) {
                animationPhase = "drop";
                dropStartY = models.coin.position.y;
                dropProgress = 0;
            }

            // Get position along curve
            const position = coinCurve.getPoint(coinProgress);
            models.coin.position.copy(position);
        } else if (animationPhase === "drop") {
            dropProgress += 0.03;

            if (dropProgress >= 1) {
                coinMoving = false;
                coinInSlot = true;
                rotateHandle();
            }

            models.coin.position.y = dropStartY - dropProgress * 0.5;
        }
    } else if (handleRotating || capsulesShaking) {
        handleProgress += 0.01;
        shakeProgress++;

        if (handleProgress >= 1) {
            if (rotationNumber == 1) {
                capsulesShaking = false;
                models.capsules.forEach((capsule, index) => {
                    capsule.position.copy(originalPositions[index]);
                });
                shakeCapsules();
                pauseStartTime = elapsedTime;
                pauseDuration = 0.25;
                pause = true;
            } else if (rotationNumber == 2) {
                handleRotating = false;
                models.coin.position.set(-0.5, -0.5, 1);

                capsulesShaking = false;
                models.capsules.forEach((capsule, index) => {
                    capsule.position.copy(originalPositions[index]);
                });
                produceToy();
            }
        }

        handleMesh.rotation.x = handleStart - handleProgress * Math.PI;

        models.capsules.forEach((capsule, index) => {
            // Each object shakes at different frequency
            const shakeAmount =
                Math.sin(shakeProgress * shakeFrequencies[index]) *
                SHAKE_INTENSITY;
            const dampen = 1 - shakeProgress / SHAKE_DURATION;

            const original = originalPositions[index];
            const direction = shakeDirections[index];

            capsule.position.x =
                original.x + direction.x * shakeAmount * dampen;
            capsule.position.y =
                original.y + direction.y * shakeAmount * dampen;
            capsule.position.z =
                original.z + direction.z * shakeAmount * dampen;
        });
    } else if (capsuleMoving || flapOpening || flapClosing || cameraMoving) {
        if (flapOpening) {
            flapProgress += 0.01;
            if (flapProgress >= 1) {
                flapOpening = false;
                flapClosing = true;
            }

            flapMesh.rotation.z = -(flapStart - flapProgress * (Math.PI / 2));
        }
        if (flapClosing) {
            // Close flap
            flapProgress -= 0.01;
            if (flapProgress <= 0) {
                flapClosing = false;
                flapProgress = 0;
            } else {
                flapMesh.rotation.z = -(
                    flapStart -
                    flapProgress * (Math.PI / 2)
                );
            }
        }

        // Move capsule with physics
        if (capsuleMoving) {
            // Apply gravity
            capsuleVelocity.y += gravity;

            // Update position
            capsule.position.x += capsuleVelocity.x;
            capsule.position.y += capsuleVelocity.y;
            capsule.position.z += capsuleVelocity.z;

            //capsule.rotation.z += capsuleAngularVelocity;
            capsule.rotateOnWorldAxis(worldZAxis, -capsuleAngularVelocity);

            // Check for ground collision (bounce)
            if (capsule.position.y <= floorY && capsuleVelocity.y < 0) {
                // Hit the ground
                capsule.position.y = floorY; // Snap to floor
                capsuleVelocity.y = -capsuleVelocity.y * bounceDamping; // Reverse and reduce velocity
                bounceCount++;

                // Reduce forward speed slightly on each bounce
                capsuleVelocity.x *= 0.98; // Changed from 0.95 to lose less speed
                capsuleAngularVelocity *= 0.98;
            }

            // Apply friction when on the ground (after bouncing stops)
            if (
                capsule.position.y <= floorY + 0.01 &&
                Math.abs(capsuleVelocity.y) < 0.01
            ) {
                // On the ground - apply rolling friction
                capsuleVelocity.x *= 0.98; // Gentle friction (0.98 = loses 2% per frame)
                capsuleAngularVelocity *= 0.98;
            }

            // Stop when nearly stopped
            if (
                bounceCount >= maxBounces &&
                Math.abs(capsuleVelocity.x) < 0.002 && // INCREASED from 0.0005
                Math.abs(capsuleVelocity.y) < 0.01 // INCREASED from 0.001
            ) {
                capsuleMoving = false;
                capsule.position.y = floorY; // Ensure it's on the floor
                capsuleRotationResetting = true;
            }
        }

        // Move camera
        camera.lookAt(capsule.position);
        const distance = camera.position.distanceTo(
            new THREE.Vector3(4.25, 0.5, -0.6)
        );
        if (distance <= 0.01) {
            cameraMoving = false;
        } else {
            camera.position.lerp(new THREE.Vector3(4.25, 0.5, -0.6), 0.03);
        }

        cameraLookAtTarget.lerp(capsule.position, 0.02); // Adjust speed (0.05 = smooth, 0.1 = faster)
        camera.lookAt(cameraLookAtTarget);
    } else if (capsuleRotationResetting) {
        const qStart = new THREE.Quaternion().setFromEuler(capsule.rotation);
        const qEnd = new THREE.Quaternion().setFromEuler(
            new THREE.Euler(Math.PI / 2, 0, 0)
        );

        if (qStart.angleTo(qEnd) < 0.01) {
            capsule.quaternion.copy(qEnd); // Snap to final position
            capsuleRotationResetting = false;
            let currentPos = new THREE.Vector3(
                capsule.position.x - 0.125,
                capsule.position.y - 1.057,
                capsule.position.z + 0.577
            );
            capsule.position.set(0, 1, 0);
            pivotCapsule.position.set(currentPos.x, currentPos.y, currentPos.z);
            capsuleOpening = true;
            capsuleOpenProgress = 0;
            openAudio.currentTime = 0;
            openAudio.play();
        } else {
            // Interpolate (alpha is 0 to 1)
            qStart.slerp(qEnd, 0.1);

            // Apply to object
            capsule.rotation.setFromQuaternion(qStart);
        }
    } else if (capsuleOpening) {
        capsuleOpenProgress += 0.03;

        if (capsuleOpenProgress >= 1) {
            capsuleOpening = false;
            openModal();
        }

        if (colourChoice == 0 || colourChoice == 1 || colourChoice == 3) {
            // Now these will open correctly because capsule rotation is reset
            capsuleTopHalf.rotation.x += capsuleOpeningSpeed;

            // Rotate right hemisphere clockwise around Z axis
            capsuleBottomHalf.rotation.x -= capsuleOpeningSpeed;
        }
        if (colourChoice == 2) {
            // Now these will open correctly because capsule rotation is reset
            capsuleTopHalf.rotation.x -= capsuleOpeningSpeed;

            // Rotate right hemisphere clockwise around Z axis
            capsuleBottomHalf.rotation.x += capsuleOpeningSpeed;
        }
    }

    // Update controls
    if (!capsulePresent) {
        controls.update();
    }

    // Render
    renderer.render(scene, camera);

    // Call tick again on the next frame
    window.requestAnimationFrame(tick);
};

const rotateHandle = () => {
    handleProgress = 0;
    handleStart = 0;
    rotationNumber = 1;
    handleRotating = true;
    handleAudio.currentTime = 0;
    handleAudio.play();
    shakeCapsules();
    capsulesAudio.currentTime = 0;
    capsulesAudio.play();
};

const shakeCapsules = () => {
    capsulesShaking = true;
    shakeProgress = 0;
    originalPositions = [];
    shakeDirections = [];
    shakeFrequencies = [];

    models.capsules.forEach((capsule) => {
        originalPositions.push(capsule.position.clone());

        const randomDir = new THREE.Vector3(
            (Math.random() - 0.5) * 2,
            (Math.random() - 0.5) * 2,
            (Math.random() - 0.5) * 2
        ).normalize();

        shakeDirections.push(randomDir);

        shakeFrequencies.push(0.2 + Math.random() * 0.4); // 0.2 to 0.6
    });
};

const produceToy = () => {
    let colours = [
        models.blueCapsule.getObjectByName("single-blue-capsule"),
        models.pinkCapsule.getObjectByName("single-pink-capsule"),
        models.greenCapsule.getObjectByName("single-green-capsule"),
        models.yellowCapsule.getObjectByName("single-yellow-capsule"),
    ];
    let pivotColours = [
        models.pivotBlueCapsule, //.getObjectByName("pivot-blue-capsule"),
        models.pivotPinkCapsule, //.getObjectByName("pivot-pink-capsule"),
        models.pivotGreenCapsule, //.getObjectByName("pivot-green-capsule"),
        models.pivotYellowCapsule, //.getObjectByName("pivot-yellow-capsule"),
    ];
    colourChoice = Math.floor(Math.random() * 4);
    //colourChoice = 2;
    capsule = colours[colourChoice];
    pivotCapsule = pivotColours[colourChoice];

    // Find both halves
    // If capsule contains two meshes
    let meshes = [];
    pivotCapsule.traverse((child) => {
        if (child.isMesh) {
            meshes.push(child);
        }
    });

    if (meshes.length >= 2) {
        capsuleTopHalf = meshes[0];
        capsuleBottomHalf = meshes[1];
    }
    // else if (meshes.length === 1) {
    //     // Capsule is the top, child is the bottom
    //     capsuleTopHalf = meshes[0];
    //     capsuleBottomHalf = capsule.children.find((child) => child.isMesh);
    // }

    capsule.position.set(0.8, 0.443, -0.58);
    capsule.rotation.x = Math.random() * Math.PI * 2;
    capsule.rotation.y = Math.random() * Math.PI * 2;
    capsule.rotation.z = Math.random() * Math.PI * 2;

    flapProgress = 0;
    flapStart = 0;
    flapOpening = true;
    moveToy();
};

const moveToy = () => {
    controls.enabled = false;
    capsulePresent = true;
    flapProgress = 1;
    flapClosing = true;
    cameraMoving = true;
    capsuleMoving = true;

    cameraLookAtTarget.copy(controls.target);

    capsuleProgress = 0;

    // Physics-based motion variables
    capsuleVelocity = {
        x: 0.03, // Forward speed (adjust for faster/slower roll)
        y: 0.02, // Vertical velocity (starts at 0, gravity will apply)
        z: 0,
    };

    capsuleAngularVelocity = 0.1; // Rotation speed for rolling effect

    bounceCount = 0;
    maxBounces = 5; // Number of times it bounces
    gravity = -0.003; // Gravity strength
    bounceDamping = 0.8; // Energy lost on each bounce (0.6 = loses 40% of velocity)
    floorY = 0.3; // Floor height (adjust based on your model)
    capsuleAudio.currentTime = 0;
    capsuleAudio.play();
};

//REGULAR PAGE FUNCTIONALITY
const modalOverlay = document.getElementById("modalOverlay");
const closeButton = document.getElementById("close-btn");
const tryAgainButton = document.getElementById("try-again-button");
const viewButton = document.getElementById("view-button");

modalOverlay.addEventListener("click", (e) => {
    closeModalOnOverlay(e);
});

closeButton.addEventListener("click", (e) => {
    closeModal();
});
tryAgainButton.addEventListener("click", (e) => {
    closeModal();
});

const openModal = () => {
    modalOverlay.classList.remove("closing");
    modalOverlay.classList.add("active");
    let projectChoice = Math.floor(Math.random() * 4);
    document.getElementById("home-card-h").textContent =
        projectCards[projectChoice].title;
    document.getElementById("home-card-d").textContent =
        projectCards[projectChoice].description;
    document.getElementById("home-card-t-1").textContent =
        projectCards[projectChoice].tags[0];
    document.getElementById("home-card-t-2").textContent =
        projectCards[projectChoice].tags[1];
    document.getElementById("home-card-t-3").textContent =
        projectCards[projectChoice].tags[2];
    document.getElementById("home-card-img").src =
        projectCards[projectChoice].imagePath;
    document.getElementById("home-card-img").alt =
        projectCards[projectChoice].altText;
    viewButton.addEventListener("click", (e) => {
        closeModal();
        navigateTo(projectCards[projectChoice].buttonDestination);
    });
};

const closeModal = () => {
    modalOverlay.classList.add("closing");
    camera.position.set(5, 2.25, 0);
    capsulePresent = false;
    controls.enabled = true;
    if (colourChoice == 0 || colourChoice == 1 || colourChoice == 3) {
        capsuleTopHalf.rotation.x = Math.PI / 2;
        capsuleBottomHalf.rotation.x = -Math.PI / 2;
    }
    if (colourChoice == 2) {
        capsuleTopHalf.rotation.x = -Math.PI / 2;
        capsuleBottomHalf.rotation.x = Math.PI / 2;
    }
    pivotCapsule.position.set(0, 1, 0);
    models.coin.position.set(0, 1, 1.75);
    coinMoving = false;
    coinInSlot = false;

    instructions.style.display = "block";
    modalOverlay.classList.remove("active", "closing");
};

const closeModalOnOverlay = (event) => {
    if (event.target === modalOverlay) {
        closeModal();
    }
};

//REST OF WEBSITE PORTION

const navLogo = document.getElementById("nav-logo");
navLogo.addEventListener("click", (e) => {
    closeModal();
    navigateTo("home");
});

const navHome = document.getElementById("nav-home");
navHome.addEventListener("click", (e) => {
    closeModal();
    navigateTo("home");
});

const navWork = document.getElementById("nav-work");
navWork.addEventListener("click", (e) => {
    navigateTo("work");
});

const navAbout = document.getElementById("nav-about");
navAbout.addEventListener("click", (e) => {
    navigateTo("about");
});

const mobileMenuBtn = document.getElementById("mobile-menu-btn");
mobileMenuBtn.addEventListener("click", (e) => {
    toggleMobileMenu();
});

const card1 = document.getElementById("card-1");
card1.addEventListener("click", (e) => {
    navigateTo("ukwild");
});

const card2 = document.getElementById("card-2");
card2.addEventListener("click", (e) => {
    navigateTo("tutankhamuns-tomb");
});

const card3 = document.getElementById("card-3");
card3.addEventListener("click", (e) => {
    navigateTo("carter-cabinet");
});

const card4 = document.getElementById("card-4");
card4.addEventListener("click", (e) => {
    navigateTo("harmonic-park");
});

const backToWork1 = document.getElementById("back-1");
backToWork1.addEventListener("click", (e) => {
    navigateTo("work");
});

const backToWork2 = document.getElementById("back-2");
backToWork2.addEventListener("click", (e) => {
    navigateTo("work");
});

const backToWork3 = document.getElementById("back-3");
backToWork3.addEventListener("click", (e) => {
    navigateTo("work");
});

const backToWork4 = document.getElementById("back-4");
backToWork4.addEventListener("click", (e) => {
    navigateTo("work");
});

// Navigation functionality
const navigateTo = (page) => {
    // Hide all pages
    const pages = document.querySelectorAll(".page-section");
    pages.forEach((p) => p.classList.remove("active"));

    // Show selected page
    document.getElementById(page).classList.add("active");

    // Update active nav link
    const navLinks = document.querySelectorAll(".nav-link");
    navLinks.forEach((link) => {
        link.classList.remove("active");
        if (link.getAttribute("data-page") === page) {
            link.classList.add("active");
        }
    });

    // Close mobile menu if open
    const navLinksElement = document.getElementById("navLinks");
    navLinksElement.classList.remove("mobile-open");

    // Scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });
};

// Mobile menu toggle
const toggleMobileMenu = () => {
    const navLinks = document.getElementById("navLinks");
    navLinks.classList.toggle("mobile-open");
};

// Hide loading screen after animation
// setTimeout(() => {
//     const loadingScreen = document.getElementById("loadingScreen");
//     if (loadingScreen) {
//         loadingScreen.style.display = "none";
//     }
// }, 2800);

// Close mobile menu when clicking outside
document.addEventListener("click", (e) => {
    const navLinks = document.getElementById("navLinks");
    const mobileMenuBtn = document.querySelector(".mobile-menu-btn");

    if (!navLinks.contains(e.target) && !mobileMenuBtn.contains(e.target)) {
        navLinks.classList.remove("mobile-open");
    }
});

// Carousel functionality

const carouselNavPrev1 = document.getElementById("carousel-nav-prev-1");
carouselNavPrev1.addEventListener("click", (e) => {
    carouselPrev("ukwild");
});

const carouselNavNext1 = document.getElementById("carousel-nav-next-1");
carouselNavNext1.addEventListener("click", (e) => {
    carouselNext("ukwild");
});

const carouselNavPrev2 = document.getElementById("carousel-nav-prev-2");
carouselNavPrev2.addEventListener("click", (e) => {
    carouselPrev("tutankhamuns-tomb");
});

const carouselNavNext2 = document.getElementById("carousel-nav-next-2");
carouselNavNext2.addEventListener("click", (e) => {
    carouselNext("tutankhamuns-tomb");
});

const carouselNavPrev3 = document.getElementById("carousel-nav-prev-3");
carouselNavPrev3.addEventListener("click", (e) => {
    carouselPrev("carter-cabinet");
});

const carouselNavNext3 = document.getElementById("carousel-nav-next-3");
carouselNavNext3.addEventListener("click", (e) => {
    carouselNext("carter-cabinet");
});

const carouselNavPrev4 = document.getElementById("carousel-nav-prev-4");
carouselNavPrev4.addEventListener("click", (e) => {
    carouselPrev("harmonic-park");
});

const carouselNavNext4 = document.getElementById("carousel-nav-next-4");
carouselNavNext4.addEventListener("click", (e) => {
    carouselNext("harmonic-park");
});

const carouselPrev = (projectId) => {
    const track = document.querySelector(`#${projectId} .carousel-track`);
    const slides = document.querySelectorAll(`#${projectId} .carousel-slide`);
    const indicators = document.querySelectorAll(
        `#${projectId} .carousel-indicator`
    );
    const currentIndex = Array.from(slides).findIndex((slide) =>
        slide.classList.contains("active")
    );
    const newIndex = (currentIndex - 1 + slides.length) % slides.length;

    track.style.transform = `translateX(-${newIndex * 100}%)`;
    slides.forEach((slide, index) =>
        slide.classList.toggle("active", index === newIndex)
    );
    indicators.forEach((indicator, index) =>
        indicator.classList.toggle("active", index === newIndex)
    );
};

const carouselNext = (projectId) => {
    const track = document.querySelector(`#${projectId} .carousel-track`);
    const slides = document.querySelectorAll(`#${projectId} .carousel-slide`);
    const indicators = document.querySelectorAll(
        `#${projectId} .carousel-indicator`
    );
    const currentIndex = Array.from(slides).findIndex((slide) =>
        slide.classList.contains("active")
    );
    const newIndex = (currentIndex + 1) % slides.length;

    track.style.transform = `translateX(-${newIndex * 100}%)`;
    slides.forEach((slide, index) =>
        slide.classList.toggle("active", index === newIndex)
    );
    indicators.forEach((indicator, index) =>
        indicator.classList.toggle("active", index === newIndex)
    );
};

const carouselGoTo = (projectId, index) => {
    const track = document.querySelector(`#${projectId} .carousel-track`);
    const slides = document.querySelectorAll(`#${projectId} .carousel-slide`);
    const indicators = document.querySelectorAll(
        `#${projectId} .carousel-indicator`
    );

    track.style.transform = `translateX(-${index * 100}%)`;
    slides.forEach((slide, i) => slide.classList.toggle("active", i === index));
    indicators.forEach((indicator, i) =>
        indicator.classList.toggle("active", i === index)
    );
};

// Lightbox functionality
document.addEventListener("DOMContentLoaded", () => {
    const slides = document.querySelectorAll(".carousel-slide");
    const lightbox = document.querySelector(".lightbox");
    const lightboxImage = document.querySelector(".lightbox-image");
    const lightboxClose = document.querySelector(".lightbox-close");
    const lightboxNavPrev = document.querySelector(".lightbox-nav.prev");
    const lightboxNavNext = document.querySelector(".lightbox-nav.next");

    // Get all images from slides
    const images = Array.from(slides)
        .map((slide) => slide.querySelector("img"))
        .filter((img) => img);
    let currentIndex = 0;

    // Open lightbox when clicking on a slide
    slides.forEach((slide, index) => {
        slide.addEventListener("click", (e) => {
            // Don't open if clicking on carousel navigation buttons
            if (e.target.closest(".carousel-nav")) return;

            const img = slide.querySelector("img");
            if (img) {
                currentIndex = index;
                lightboxImage.src = img.src;
                lightboxImage.alt = img.alt;
                lightbox.classList.add("active");
                document.body.style.overflow = "hidden"; // Prevent background scrolling
            }
        });
    });

    // Close lightbox
    if (lightboxClose) {
        lightboxClose.addEventListener("click", () => {
            lightbox.classList.remove("active");
            document.body.style.overflow = "";
        });
    }

    // Close on background click
    if (lightbox) {
        lightbox.addEventListener("click", (e) => {
            if (e.target === lightbox) {
                lightbox.classList.remove("active");
                document.body.style.overflow = "";
            }
        });
    }

    // Previous image
    if (lightboxNavPrev) {
        lightboxNavPrev.addEventListener("click", () => {
            currentIndex = (currentIndex - 1 + images.length) % images.length;
            lightboxImage.src = images[currentIndex].src;
            lightboxImage.alt = images[currentIndex].alt;
        });
    }

    // Next image
    if (lightboxNavNext) {
        lightboxNavNext.addEventListener("click", () => {
            currentIndex = (currentIndex + 1) % images.length;
            lightboxImage.src = images[currentIndex].src;
            lightboxImage.alt = images[currentIndex].alt;
        });
    }

    // Keyboard navigation
    document.addEventListener("keydown", (e) => {
        if (!lightbox.classList.contains("active")) return;

        if (e.key === "Escape") {
            lightbox.classList.remove("active");
            document.body.style.overflow = "";
        }
        if (e.key === "ArrowLeft") {
            currentIndex = (currentIndex - 1 + images.length) % images.length;
            lightboxImage.src = images[currentIndex].src;
            lightboxImage.alt = images[currentIndex].alt;
        }
        if (e.key === "ArrowRight") {
            currentIndex = (currentIndex + 1) % images.length;
            lightboxImage.src = images[currentIndex].src;
            lightboxImage.alt = images[currentIndex].alt;
        }
    });
});

//Setting Card Text
document.getElementById("card-1-h").textContent = projectCards[0].title;
document.getElementById("card-1-d").textContent = projectCards[0].description;
document.getElementById("card-1-t-1").textContent = projectCards[0].tags[0];
document.getElementById("card-1-t-2").textContent = projectCards[0].tags[1];
document.getElementById("card-1-t-3").textContent = projectCards[0].tags[2];
document.getElementById("card-1-img").src = projectCards[0].imagePath;
document.getElementById("card-1-img").alt = projectCards[0].altText;

document.getElementById("card-2-h").textContent = projectCards[1].title;
document.getElementById("card-2-d").textContent = projectCards[1].description;
document.getElementById("card-2-t-1").textContent = projectCards[1].tags[0];
document.getElementById("card-2-t-2").textContent = projectCards[1].tags[1];
document.getElementById("card-2-t-3").textContent = projectCards[1].tags[2];
document.getElementById("card-2-img").src = projectCards[1].imagePath;
document.getElementById("card-2-img").alt = projectCards[1].altText;

document.getElementById("card-3-h").textContent = projectCards[2].title;
document.getElementById("card-3-d").textContent = projectCards[2].description;
document.getElementById("card-3-t-1").textContent = projectCards[2].tags[0];
document.getElementById("card-3-t-2").textContent = projectCards[2].tags[1];
document.getElementById("card-3-t-3").textContent = projectCards[2].tags[2];
document.getElementById("card-3-img").src = projectCards[2].imagePath;
document.getElementById("card-3-img").alt = projectCards[2].altText;

document.getElementById("card-4-h").textContent = projectCards[3].title;
document.getElementById("card-4-d").textContent = projectCards[3].description;
document.getElementById("card-4-t-1").textContent = projectCards[3].tags[0];
document.getElementById("card-4-t-2").textContent = projectCards[3].tags[1];
document.getElementById("card-4-t-3").textContent = projectCards[3].tags[2];
document.getElementById("card-4-img").src = projectCards[3].imagePath;
document.getElementById("card-4-img").alt = projectCards[3].altText;
